/**
 *
 * @module dustSink
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Services from './../../services/entry';
import TelemetryManager from './../telemetry/telemetryManager';
import DustEvent from './dustEvent';

const Util = Services.Util;
const Dust = Services.Internal.Dust;
const DustServerPayload = Dust.DustServerPayload;
const DustClientPayload = Dust.DustClientPayload;
const DustDevicePayload = Dust.DustDevicePayload;
const DustApplicationPayload = Dust.DustApplicationPayload;
const DustSdkPayload = Dust.DustSdkPayload;
const DustCategory = Dust.DustCategory;
const LogSink = Logging.LogSink;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    nonEmptyObject,
    instanceStrict
} = Check.assert;

/**
 *
 * @class DustSink
 * @desc Sink implementation that logs to Dust Service via TelemetryManager.postEvent
 * @extends {LogSink}
 *
 */
export default class DustSink extends LogSink {

    constructor() {

        super();

        /**
         *
         * @access private
         * @type {Array}
         * @desc holds dustEvents until the TelemetryManager is available and then it is no longer used
         *
         */
        this.queue = [];

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.TelemetryManager}
         *
         */
        this.telemetryManager = null;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc true when TelemetryManager.eventBuffer is ready for use
         *
         */
        this.eventBufferReady = false;

        /**
         *
         * @access private
         * @type {Object}
         * @desc static environment properties for dust reporting
         *
         */
        this.environmentConfig = {};

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc determines if dust logs should be queued and sent to the Telemetry service
         *
         */
        this.disabled = false;

    }

    /**
     *
     * @access public
     * @param {Object} event - The event to be logged.
     * @desc receives an event from logger.log and stores it in a queue or creates a dust event and posts it to
     * either telemetryManager.eventBuffer.postEvent or telemetryManager.trackingBuffer.postEvent, depending
     * on the event's category.
     * @returns {Promise<Void>}
     *
     */
    log(event) {

        const { disabled, eventBufferReady, queue } = this;

        if (event.isPublic && !disabled) {

            if (eventBufferReady) {
                this.postEvent(event);
            } else {
                queue.push(event);
            }

        }

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @param {TelemetryManager} telemetryManager
     * @param {Object} environmentConfig
     * @desc lazy loads the telemetryManager into the dustSink and empties the queue
     *
     */
    enableDustSink(telemetryManager, environmentConfig) {

        const ctor = 'DustSink#enableDustSink(telemetryManager, environmentConfig)';
        const telemetryManagerError = `${ctor} needs TelemetryManager instance`;
        const environmentConfigError = `${ctor} needs environmentConfig Object`;

        nonEmptyObject(environmentConfig, environmentConfigError, InvalidArgumentException);
        instanceStrict(telemetryManager, TelemetryManager, telemetryManagerError, InvalidArgumentException);

        const { queue } = this;

        this.telemetryManager = telemetryManager;
        this.environmentConfig = environmentConfig;
        this.eventBufferReady = true;

        while (queue.length) {
            this.postEvent(queue.shift());
        }

    }

    /**
     *
     * @access public
     * @desc disabled the dust sink and wipes out the queue, called from sdkSession.initialize
     *
     */
    disableDustSink() {

        this.disabled = true;
        this.queue = [];

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc creates a dust event
     * @note currently we do not support `transactionId` and `correlationIds`
     * we also do not have the ability to provide a full `SDK.Services.Internal.DustApplicationPayload`
     * @returns {SDK.Internal.Dust.DustEvent}
     *
     */
    createDustEvent(event) {

        const { environmentConfig } = this;

        const server = Util.getSafe(() => event.extraData.server);
        const data = Object.assign({}, Util.getSafe(() => event.extraData.data));
        const dustApplicationPayload = new DustApplicationPayload();

        const dustDevicePayload = new DustDevicePayload(
            Util.getSafe(() => environmentConfig.device.os),
            Util.getSafe(() => environmentConfig.device.model)
        );

        const dustSdkPayload = new DustSdkPayload(
            Util.getSafe(() => environmentConfig.sdk.version),
            Util.getSafe(() => environmentConfig.sdk.platform)
        );

        let dustServerPayload = {};

        if (Util.getSafe(() => event.extraData.errors)) {
            data.errors = event.extraData.errors;
        }

        if (Check.nonEmptyObject(server)) {

            const { host, path, method, statusCode, requestId, roundTripTime } = server;

            dustServerPayload = new DustServerPayload({
                host,
                path,
                method,
                statusCode,
                requestId,
                roundTripTime
            });

        }

        const dustClientPayload = new DustClientPayload({
            application: dustApplicationPayload,
            device: dustDevicePayload,
            sdk: dustSdkPayload,
            category: Util.getSafe(() => event.extraData.category),
            data,
            event: event.message,
            timestamp: Util.getSafe(() => event.extraData.timestamp)
        });

        return new DustEvent(dustClientPayload, dustServerPayload);

    }

    /**
     *
     * @access private
     * @param {Object} event - The event to be posted.
     * @desc calls postEvent on the EventBuffer that is appropriate for the event supplied. Depending on the
     * event's category, this could be either the telemetryManager.eventBuffer or telemetryManager.trackingBuffer.
     * @note telemetryManager.trackingBuffer.postEvent and telemetryManager.eventBuffer.postEvent fire
     * asynchronous methods but we do not wait for them to resolve.
     *
     */
    postEvent(event) {

        const { telemetryManager } = this;

        const dustEvent = this.createDustEvent(event);
        const eventCategory = Util.getSafe(() => event.extraData.category);

        if (eventCategory === DustCategory.glimpse) {
            telemetryManager.trackingBuffer.postEvent(dustEvent);
        } else {
            telemetryManager.eventBuffer.postEvent(dustEvent);
        }

    }

}



// WEBPACK FOOTER //
// ./src/internal/dust/dustSink.js